import React, { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { FIELD_PRODUCT_ERROR, useValidationContext } from '@context/ValidationContext';
import { ReactFCV } from '@types';
import { EScrollRegistryKeys, registerScrollCallback } from '@utils/scrollRegistry';

import { IAdditionalProductLabelProps } from '../../model';

import { StyledAdditionalProductLabelLink, StyledDedication, StyledWithDedication } from './styled';
import { getAdditionalLabelText } from './utils';

export const AdditionalProductLabel: ReactFCV<IAdditionalProductLabelProps> = ({
  disabled,
  maximumQuantity,
  itemQuantity,
  messages,
  isEngraving,
  withDedication,
  onClick,
  ...rest
}) => {
  const additionalLabelText = getAdditionalLabelText({
    disabled,
    maximumQuantity,
    itemQuantity,
    withDedication,
    messages,
    onClick,
  });
  const { reportError, clearError } = useValidationContext();
  const { formatMessage } = useIntl();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hasProductError =
      additionalLabelText &&
      (additionalLabelText.id === messages.changeProductQuantity?.id ||
        additionalLabelText.id === messages.removeProduct?.id);

    if (hasProductError && ref.current) {
      registerScrollCallback(EScrollRegistryKeys.PRODUCT_ERROR_MESSAGE, () => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      reportError(FIELD_PRODUCT_ERROR, formatMessage(additionalLabelText));
    } else {
      clearError(FIELD_PRODUCT_ERROR);
    }
  }, [
    additionalLabelText,
    clearError,
    formatMessage,
    messages?.changeProductQuantity,
    messages?.removeProduct,
    reportError,
  ]);

  if (!additionalLabelText) {
    return null;
  }

  return (
    <div ref={ref}>
      {withDedication ? (
        <StyledWithDedication {...rest}>
          <FormattedMessage {...additionalLabelText} />: <StyledDedication>{withDedication}</StyledDedication>
        </StyledWithDedication>
      ) : (
        <StyledAdditionalProductLabelLink clickable={Boolean(onClick)} disabled={disabled} onClick={onClick} {...rest}>
          <FormattedMessage {...additionalLabelText} values={{ quantity: <span>{maximumQuantity}</span> }} />
        </StyledAdditionalProductLabelLink>
      )}
    </div>
  );
};
